import { FileActionMenuContext } from "../../file-action/file-action-menu-context";
import { createElement, useEffect, useState, useRef, useMemo } from "react";
import { Module, SubMenu } from "../../../models/menu/menu.model";
import { getComponentForRoute, getUrlPathFromComponentPath } from "./routeMapping";
import menuService from "../../../services/menu/menu.service";
import AuthService from "../../../services/autentication/auth.service";
import { MenuRoute } from "./types";

/**
 * Builds route configuration from menu item
 * Uses displayName to generate user-friendly URLs
 */
const createRouteFromMenuItem = (
  route: string,
  displayName: string,
  seenPaths: Set<string>,
  menuId: number
): MenuRoute | null => {
  // Generate URL based on displayName instead of component path
  const urlPath = getUrlPathFromComponentPath(route, displayName);
  
  // Skip duplicates
  if (seenPaths.has(urlPath)) return null;
  
  const Component = getComponentForRoute(route);
  if (!Component) return null;
  
  seenPaths.add(urlPath);
  const ScopedComponent = () => createElement(FileActionMenuContext.Provider, { value: menuId }, createElement(Component));
  return { path: urlPath, component: ScopedComponent, displayName };
};

/**
 * Processes submenus to generate routes
 */
const processSubMenus = (
  subMenus: SubMenu[],
  seenPaths: Set<string>
): MenuRoute[] => {
  return subMenus
    .filter((sm) => sm.permissions?.canView && sm.route)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((sm) => createRouteFromMenuItem(sm.route!, sm.displayName, seenPaths, sm.menuId))
    .filter((route): route is MenuRoute => route !== null);
};

/**
 * Generates routes from modules data
 */
const generateRoutes = (modules: Module[]): MenuRoute[] => {
  const routes: MenuRoute[] = [];
  const seenPaths = new Set<string>();

  const sortedModules = [...modules].sort((a, b) => a.displayOrder - b.displayOrder);

  for (const module of sortedModules) {
    const sortedMenus = [...module.menus].sort((a, b) => a.displayOrder - b.displayOrder);

    for (const menu of sortedMenus) {
      const hasSubMenus = Array.isArray(menu.subMenu) && menu.subMenu.length > 0;

      if (hasSubMenus) {
        routes.push(...processSubMenus(menu.subMenu, seenPaths));
      } else if (menu.permissions?.canView && menu.route) {
        const route = createRouteFromMenuItem(menu.route, menu.displayName, seenPaths, menu.menuId);
        if (route) routes.push(route);
      }
    }
  }

  return routes;
};

/**
 * Custom hook to generate dynamic routes from menu data
 */
export const MenuRoutes = () => {
  const [routes, setRoutes] = useState<MenuRoute[]>([]);
  const [loading, setLoading] = useState(false);
  const [menuData, setMenuData] = useState<Module[]>([]);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const loadRoutes = async () => {
      // Not authenticated, skip API call
      if (!AuthService.isUserLoggedIn()) {
        setMenuData([]);
        setRoutes([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const data = await menuService.getUserMenusAsync();

        // Check if component is still mounted
        if (!mountedRef.current) return;

        if (data?.length > 0) {
          setMenuData(data);
          setRoutes(generateRoutes(data));
        } else {
          setMenuData([]);
          setRoutes([]);
          setError("No menu data available");
        }
      } catch (err) {
        if (!mountedRef.current) return;

        console.error("[MenuRoutes] Error loading menu data:", err);
        setMenuData([]);
        setRoutes([]);
        setError(err instanceof Error ? err.message : "Failed to load menu data");
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };

    loadRoutes();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  return useMemo(
    () => ({ routes, loading, menuData, error }),
    [routes, loading, menuData, error]
  );
};
